import {
  createSlice,
  isFulfilled,
  isPending,
  isRejected,
} from '@reduxjs/toolkit';

import { IStudentFlexibleRateVersion } from '../StudentFlexibleRateVersionsSlice';
import { IFlexiblePlan } from '../FlexiblePlansSlice';
import { IUser } from '../../UserSlice';
import { RootState } from '../..';
import {
  adminCreateStudentFlexibleRate,
  adminDeleteStudentFlexibleRate,
  adminGetStudentFlexibleRates,
  adminUpdateStudentFlexibleRate,
  getUserFlexibleRatesAsAdmin,
} from './adminApi';

export interface IStudentFlexibleRateAmount {
  studentFlexibleRateVersion: string | IStudentFlexibleRateVersion;
  amount: number;
  currency: string;
}

export interface IStudentFlexibleRate {
  _id: string;
  package: string | IFlexiblePlan;
  amounts: Array<IStudentFlexibleRateAmount>;
  createdBy: string | IUser;
  updatedBy: string | IUser;
  country?: string;
  curriculum?: string;
  grade?: string;
}

export interface IInitialStudentFlexibleRatesState {
  status: 'idle' | 'loading' | 'rejected';
  // Admin
  adminStudentFlexibleRateListData?: {
    limit?: number;
    offset?: number;
    totalDocs?: number;
    list: Array<IStudentFlexibleRate>;
  };
  adminUserFlexibleRates: IStudentFlexibleRate[];
}

const initialState: IInitialStudentFlexibleRatesState = {
  status: 'idle',
  adminStudentFlexibleRateListData: undefined,
  adminUserFlexibleRates: [],
};

export const StudentFlexibleRateVersionsSlice = createSlice({
  initialState,
  name: 'studentFlexibleRateVersionsSlice',
  reducers: {
    resetAdminStudentFlexibleRateListData: (state) => {
      state.adminStudentFlexibleRateListData = undefined;
    },
    resetAdminUserFlexibleRates: (state) => {
      state.adminUserFlexibleRates = [];
    },
  },
  extraReducers(builder) {
    builder
      .addCase(adminGetStudentFlexibleRates.fulfilled, (state, action) => {
        const result = action.payload;

        const newAdminStudentFlexibleRateListData = {
          limit: result.limit,
          offset: result.offset,
          totalDocs: result.totalDocs || 0,
          list: result.docs || [],
        };

        state.adminStudentFlexibleRateListData = newAdminStudentFlexibleRateListData;
      })
      .addCase(adminCreateStudentFlexibleRate.fulfilled, (state, action) => {
        const result = action.payload;

        if (state.adminStudentFlexibleRateListData) {
          const newAdminStudentFlexibleRateListData = {
            ...state.adminStudentFlexibleRateListData,
          };

          newAdminStudentFlexibleRateListData.list.unshift(result);

          if (newAdminStudentFlexibleRateListData.totalDocs) {
            newAdminStudentFlexibleRateListData.totalDocs += 1;
          } else {
            newAdminStudentFlexibleRateListData.totalDocs = 1;
          }

          state.adminStudentFlexibleRateListData = newAdminStudentFlexibleRateListData;
        }
      })
      .addCase(adminUpdateStudentFlexibleRate.fulfilled, (state, action) => {
        const result = action.payload;

        if (
          state.adminStudentFlexibleRateListData &&
          state.adminStudentFlexibleRateListData.list &&
          state.adminStudentFlexibleRateListData.list.length
        ) {
          const newAdminStudentFlexibleRateListData = {
            ...state.adminStudentFlexibleRateListData,
          };

          const index = newAdminStudentFlexibleRateListData.list.findIndex(
            (item) => item._id === result._id
          );

          if (index > -1) {
            newAdminStudentFlexibleRateListData.list[index] = result;
          }

          state.adminStudentFlexibleRateListData = newAdminStudentFlexibleRateListData;
        }
      })
      .addCase(adminDeleteStudentFlexibleRate.fulfilled, (state, action) => {
        const result = action.payload;

        if (
          state.adminStudentFlexibleRateListData &&
          state.adminStudentFlexibleRateListData.list &&
          state.adminStudentFlexibleRateListData.list.length
        ) {
          const newAdminStudentFlexibleRateListData = {
            ...state.adminStudentFlexibleRateListData,
          };

          newAdminStudentFlexibleRateListData.totalDocs = newAdminStudentFlexibleRateListData.totalDocs
            ? newAdminStudentFlexibleRateListData.totalDocs - 1
            : 0;

          newAdminStudentFlexibleRateListData.list = newAdminStudentFlexibleRateListData.list.filter(
            (item) => item._id !== result._id
          );

          state.adminStudentFlexibleRateListData = newAdminStudentFlexibleRateListData;
        }
      })
      .addCase(getUserFlexibleRatesAsAdmin.fulfilled, (state, action) => {
        const result = action.payload;

        state.adminUserFlexibleRates = result || [];
      });

    // Common Matchers
    builder.addMatcher(
      isPending(
        adminCreateStudentFlexibleRate,
        adminGetStudentFlexibleRates,
        adminUpdateStudentFlexibleRate,
        adminDeleteStudentFlexibleRate,
        getUserFlexibleRatesAsAdmin
      ),
      (state) => {
        state.status = 'loading';
      }
    );

    builder.addMatcher(
      isFulfilled(
        adminCreateStudentFlexibleRate,
        adminGetStudentFlexibleRates,
        adminUpdateStudentFlexibleRate,
        adminDeleteStudentFlexibleRate,
        getUserFlexibleRatesAsAdmin
      ),
      (state) => {
        state.status = 'idle';
      }
    );

    builder.addMatcher(
      isRejected(
        adminCreateStudentFlexibleRate,
        adminGetStudentFlexibleRates,
        adminUpdateStudentFlexibleRate,
        adminDeleteStudentFlexibleRate,
        getUserFlexibleRatesAsAdmin
      ),
      (state) => {
        state.status = 'rejected';
      }
    );
  },
});

export const getStudentFlexibleRatesState = (
  state: RootState
): RootState['studentFlexibleRatesState'] => state.studentFlexibleRatesState;

export const {
  resetAdminStudentFlexibleRateListData,
  resetAdminUserFlexibleRates,
} = StudentFlexibleRateVersionsSlice.actions;

export * from './adminApi';
export default StudentFlexibleRateVersionsSlice.reducer;
